import type { HistoricalTimelineDecade } from "../generated-types";
import { getYearGroups, normalizeCategoryKey, normalizeTimelineYearName, timelineYearNamesMatch } from "./timeline-utils";

export const TIMELINE_URL_PARAMS = {
	decade: "timelineDecade",
	year: "timelineYear",
	slide: "timelineSlide",
	category: "timelineCategory",
} as const;

export type TimelineUrlState = {
	decadeName?: string;
	yearName?: string;
	slideIndex?: number;
	categoryKeys: string[];
};

export type TimelineDetailSelection = {
	yearName: string | null;
	eventIndex: number;
};

export function parseTimelineUrlState(searchParams?: URLSearchParams): TimelineUrlState {
	const params = searchParams ?? new URL(window.location.href).searchParams;
	const decadeName = params.get(TIMELINE_URL_PARAMS.decade)?.trim() || undefined;
	const yearName = params.get(TIMELINE_URL_PARAMS.year)?.trim() || undefined;
	const slideRaw = params.get(TIMELINE_URL_PARAMS.slide);
	const categoryRaw = params.get(TIMELINE_URL_PARAMS.category);

	let slideIndex: number | undefined;
	if (slideRaw !== null && slideRaw !== "") {
		const parsed = Number.parseInt(slideRaw, 10);
		if (Number.isFinite(parsed) && parsed >= 0) {
			slideIndex = parsed;
		}
	}

	const categoryKeys = categoryRaw
		? categoryRaw
			.split(",")
			.map((key) => key.trim())
			.filter(Boolean)
		: [];

	return {
		decadeName,
		yearName,
		slideIndex,
		categoryKeys,
	};
}

export function filterValidCategoryKeys(
	categoryKeys: string[],
	availableCategoryKeys: string[]
): string[] {
	const available = new Set(availableCategoryKeys.map((key) => normalizeCategoryKey(key)));

	return categoryKeys.filter((key) => available.has(normalizeCategoryKey(key)));
}

export function getFilterCategoryKeysFromRoot(root: HTMLElement | null): string[] {
	if (!root) {
		return [];
	}

	return Array.from(
		root.querySelectorAll<HTMLButtonElement>(".timeline-filter__option[data-category-key]")
	)
		.map((option) => option.dataset.categoryKey ?? "")
		.filter(Boolean);
}

export function findDecadeIndexByName(
	decades: HistoricalTimelineDecade[],
	decadeName?: string
): number | null {
	if (!decadeName) {
		return null;
	}

	const normalizedName = decadeName.trim().toLowerCase();
	const index = decades.findIndex(
		(decade) => (decade.name ?? "").trim().toLowerCase() === normalizedName
	);

	return index >= 0 ? index : null;
}

export function resolveDetailSelection(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[],
	yearName?: string,
	slideIndex?: number
): TimelineDetailSelection {
	const yearGroups = getYearGroups(decade, activeCategoryKeys);
	if (yearGroups.length === 0) {
		return { yearName: null, eventIndex: 0 };
	}

	const requestedYear = normalizeTimelineYearName(yearName);
	let selectedGroup = requestedYear
		? yearGroups.find((group) => timelineYearNamesMatch(group.yearName, requestedYear))
		: undefined;

	if (!selectedGroup) {
		selectedGroup = yearGroups[0];
	}

	let eventIndex = slideIndex ?? 0;
	if (!Number.isFinite(eventIndex) || eventIndex < 0 || eventIndex >= selectedGroup.dotItems.length) {
		eventIndex = 0;
	}

	return {
		yearName: selectedGroup.yearName ?? null,
		eventIndex,
	};
}

export function buildTimelineUrlState(options: {
	activeCategoryKeys: string[];
	decadeName?: string;
	yearName?: string | null;
	eventIndex?: number;
}): TimelineUrlState {
	const { activeCategoryKeys, decadeName, yearName, eventIndex = 0 } = options;

	if (!decadeName) {
		return { categoryKeys: [...activeCategoryKeys] };
	}

	return {
		categoryKeys: [...activeCategoryKeys],
		decadeName,
		yearName: yearName ?? undefined,
		slideIndex: eventIndex > 0 ? eventIndex : undefined,
	};
}

export function getTimelineShareUrl(state: TimelineUrlState): string {
	const url = new URL(window.location.href);
	applyTimelineUrlStateToSearchParams(url.searchParams, state);
	return url.toString();
}

export function replaceTimelineUrlState(state: TimelineUrlState): void {
	const url = new URL(window.location.href);
	applyTimelineUrlStateToSearchParams(url.searchParams, state);

	if (url.toString() === window.location.href) {
		return;
	}

	try {
		window.history.replaceState(null, "", url.toString());
	} catch {
		// Some app webviews restrict History API updates; URL sync is non-critical.
	}
}

function applyTimelineUrlStateToSearchParams(
	searchParams: URLSearchParams,
	state: TimelineUrlState
): void {
	Object.values(TIMELINE_URL_PARAMS).forEach((key) => {
		searchParams.delete(key);
	});

	if (state.categoryKeys.length > 0) {
		searchParams.set(TIMELINE_URL_PARAMS.category, state.categoryKeys.join(","));
	}

	if (state.decadeName) {
		searchParams.set(TIMELINE_URL_PARAMS.decade, state.decadeName);
	}

	if (state.decadeName && state.yearName) {
		searchParams.set(TIMELINE_URL_PARAMS.year, state.yearName);
	}

	if (state.decadeName && state.yearName && state.slideIndex !== undefined && state.slideIndex > 0) {
		searchParams.set(TIMELINE_URL_PARAMS.slide, String(state.slideIndex));
	}
}
